import React, { useEffect } from 'react';
import { format } from 'date-fns';
import TaskForm from './TaskForm';
import Button from '../Common/Button';

const TaskModal = ({ isOpen, onClose, task, mode, onSave, onEdit, onDelete, initialDate }) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const renderViewMode = () => {
        const getStatusLabel = (status) => {
            const labels = {
                'not-started': 'Not Started',
                'in-progress': 'In Progress',
                'completed': 'Completed',
                'blocked': 'Blocked'
            };
            return labels[status] || 'Not Started';
        };

        const getStatusColor = (status) => {
            const colors = {
                'not-started': 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
                'in-progress': 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200',
                'completed': 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200',
                'blocked': 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
            };
            return colors[status] || colors['not-started'];
        };

        return (
            <div className="space-y-3 sm:space-y-4">
                <div>
                    <h3 className={`text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 pr-8 ${(task.completed || task.status === 'completed') ? 'line-through opacity-60' : ''}`}>
                        {task.task}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        <span className={`inline-block px-2.5 sm:px-3 py-1 rounded text-xs sm:text-sm font-medium ${getStatusColor(task.status || 'not-started')}`}>
                            {getStatusLabel(task.status || 'not-started')}
                        </span>
                        <span className={`inline-block px-2.5 sm:px-3 py-1 rounded text-xs sm:text-sm font-medium ${task.priority === 'high' ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-100' :
                            task.priority === 'medium' ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100' :
                                task.priority === 'low' ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-100' :
                                    'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-100'
                            }`}>
                            {task.priority || 'normal'} priority
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Owner</label>
                        <p className="text-sm sm:text-base text-gray-900 dark:text-gray-100">{typeof task.owner === 'object' ? task.owner.name : task.owner}</p>
                    </div>
                    <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Due Date</label>
                        <p className="text-sm sm:text-base text-gray-900 dark:text-gray-100">{format(new Date(task.dueDate), 'MMMM d, yyyy')}</p>
                    </div>
                </div>

                {(task.completed || task.completedAt) && (
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <label className="block text-sm font-medium text-green-700 dark:text-green-400 mb-1">
                            ✓ Completed
                        </label>
                        {task.completedAt && (
                            <p className="text-sm text-green-600 dark:text-green-300">
                                {format(new Date(task.completedAt), 'MMMM d, yyyy \'at\' h:mm a')}
                            </p>
                        )}
                    </div>
                )}

                {task.acceptanceCriteria && (
                    <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Acceptance Criteria</label>
                        <p className="text-sm sm:text-base text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{task.acceptanceCriteria}</p>
                    </div>
                )}

                {task.dependencies && (
                    <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Dependencies</label>
                        <p className="text-sm sm:text-base text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{task.dependencies}</p>
                    </div>
                )}

                {task.contingencyPlan && (
                    <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Contingency Plan</label>
                        <p className="text-sm sm:text-base text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{task.contingencyPlan}</p>
                    </div>
                )}

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto">
                        Close
                    </Button>
                    <Button variant="secondary" onClick={onEdit} className="w-full sm:w-auto">
                        Edit
                    </Button>
                    <Button variant="danger" onClick={onDelete} className="w-full sm:w-auto">
                        Delete
                    </Button>
                </div>
            </div>
        );
    };

    const renderFormMode = () => (
        <div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 pr-8">
                {mode === 'add' ? 'Add New Task' : 'Edit Task'}
            </h3>
            <TaskForm
                task={task}
                onSave={onSave}
                onCancel={onClose}
                initialDate={initialDate}
            />
        </div>
    );

    return (
        <div
            className="fixed inset-0 z-50 overflow-y-auto animate-fade-in"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
        >
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Modal */}
            <div className="flex min-h-full items-end sm:items-center justify-center p-2 sm:p-4">
                <div
                    className="relative bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full p-4 sm:p-6 max-h-[95vh] overflow-y-auto animate-fade-in transition-colors duration-200"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close button */}
                    <button
                        onClick={onClose}
                        className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 touch-manipulation"
                        aria-label="Close modal"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Content */}
                    {mode === 'view' ? renderViewMode() : renderFormMode()}
                </div>
            </div>
        </div>
    );
};
export default TaskModal;